// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import type { SalesIncentiveUnit } from '../../station/stationTypes'
import Images from '../components/Images'
import Links from '../components/Links'
import ImageOverlay from '../ImageOverlay/ImageOverlay'
import MonthlyInfoPage from '../MonthlyInfoPage'
import { fetchMonthlyInfo, fetchMonthlyInfoScoreCard, fetchRestaurantPageScoreCardStats } from '../monthlyInfoQueries'
import type { MonthlyInfoCustomerPath, MonthlyInfoRestaurant, MonthlyInfoVariables } from '../monthlyInfoTypes'
import RequiresAction from '../RequiresAction'
import css from './restaurantPage.css'

const getSalesIncentiveUnitText = (salesIncentiveUnit: SalesIncentiveUnit): string =>
  salesIncentiveUnit === 'Euro' ? '€' : 'kpl'

type SalesIncentiveRowProps = {
  stationId: string
  date: string
  salesIncentiveUnit: SalesIncentiveUnit
  data: MonthlyInfoRestaurant
  canEdit: boolean
  refetch: (...args: Array<any>) => any
}

const SalesIncentiveRow = ({ stationId, date, salesIncentiveUnit, data, canEdit, refetch }: SalesIncentiveRowProps) => {
  const target = data.salesIncentiveTarget || 0
  const actual = data.totalActualSalesIncentive || 0
  const currentMonthTarget = data.currentMonthSalesIncentiveTarget || 0
  const currentMonthActual = data.currentMonthTotalActualSalesIncentive || 0
  const efficiency = actual && target ? Math.round((actual / target) * 100) : 0
  const currentMonthEfficiency =
    currentMonthActual && currentMonthTarget ? Math.round((currentMonthActual / currentMonthTarget) * 100) : 0
  return (
    <TableRow>
      <TableCell className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`monthlyInfo.heading.profitCenter${data.profitCenterType}`)}
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(target)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(actual)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: efficiency >= 100,
          [css.negativeCell]: efficiency < 100,
        })}
      >
        {efficiency} %
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(currentMonthTarget)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell className={cx(css.tableCell, css.numericCell)}>
        {Math.round(currentMonthActual)} {getSalesIncentiveUnitText(salesIncentiveUnit)}
      </TableCell>

      <TableCell
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: currentMonthEfficiency >= 100,
          [css.negativeCell]: currentMonthEfficiency < 100,
        })}
      >
        {currentMonthEfficiency} %
      </TableCell>

      <TableCell className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            monthlyInfoId={data.id}
            subject='Restaurant'
            isActionRequired={data.isActionRequired}
            refetch={refetch}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

type SalesIncentiveProps = {
  stationId: string
  previousMonth: string
  currentMonth: string
  salesIncentiveUnit: SalesIncentiveUnit
}

const SalesIncentive = ({ stationId, previousMonth, currentMonth, salesIncentiveUnit }: SalesIncentiveProps) => (
  <Query<Array<MonthlyInfoRestaurant>, MonthlyInfoVariables>
    query={fetchMonthlyInfo}
    variables={{
      stationId,
      date: currentMonth,
      type: 'Restaurant',
    }}
  >
    {({ data, error, loading, refetch }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.salesIncentive}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('monthlyInfo.title.salesIncentive', {
              month: moment(previousMonth).format('MMMM'),
            })}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table size='small' className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={css.tableHeading}>
                        {i18n.t('monthlyInfo.heading.profitCenterName')}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('monthlyInfo.heading.target', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('monthlyInfo.heading.actual', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('monthlyInfo.heading.successRate')}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('monthlyInfo.heading.currentMonthTarget', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('monthlyInfo.heading.currentMonthActual', {
                          unit: getSalesIncentiveUnitText(salesIncentiveUnit),
                        })}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('monthlyInfo.heading.currentMonthSuccessRate')}
                      </TableCell>

                      <TableCell className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('monthlyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {get(data, 'monthlyInfo', []).map((item) => (
                      <SalesIncentiveRow
                        stationId={stationId}
                        date={currentMonth}
                        salesIncentiveUnit={salesIncentiveUnit}
                        data={item}
                        canEdit={hasPermission}
                        key={item.id}
                        refetch={refetch}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

type CustomerPathRowProps = {
  stationId: string
  date: string
  data: MonthlyInfoCustomerPath
  canEdit: boolean
  refetch: (...args: Array<any>) => any
}

const CustomerPathRow = ({ stationId, date, data, canEdit, refetch }: CustomerPathRowProps) => (
  <TableRow>
    <TableCell className={cx(css.tableCell)}>{data.description}</TableCell>
    <TableCell className={cx(css.tableCell, css.actionCell)}>
      {canEdit && (
        <RequiresAction
          stationId={stationId}
          date={date}
          monthlyInfoId={data.id}
          subject='Restaurant'
          isActionRequired={data.isActionRequired}
          refetch={refetch}
        />
      )}
    </TableCell>
  </TableRow>
)

type CustomerPathProps = {
  stationId: string
  date: string
}

const CustomerPath = ({ stationId, date }: CustomerPathProps) => (
  <Query<Array<MonthlyInfoCustomerPath>, MonthlyInfoVariables>
    query={fetchMonthlyInfo}
    variables={{
      stationId,
      date,
      type: 'CustomerPath',
    }}
  >
    {({ data, error, loading, refetch }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.customerPath}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('monthlyInfo.title.customerPath')}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={css.tableHeading}>{i18n.t('monthlyInfo.heading.taskName')}</TableCell>
                      <TableCell className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('monthlyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {get(data, 'monthlyInfo', []).map((item) => (
                      <CustomerPathRow
                        stationId={stationId}
                        date={date}
                        data={item}
                        canEdit={hasPermission}
                        key={item.id}
                        refetch={refetch}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

const ScoreCardTable = ({ stationId, date }) => {
  const getColorClassName = ([category, values]: Row, type: string): string | null | undefined => {
    const { actual, high, low } = values[type]
    let className

    if (!actual && actual !== 0) {
      return null
    }

    className = css.colorCircleYellow

    if (actual >= high) {
      className = css.colorCircleGreen
    } else if (actual < low) {
      className = css.colorCircleRed
    }

    return className
  }

  const rowOrder = ['dailyChecklistUsage', 'toiletChecks', 'hourlyHallChecklistUsage']
  const orderMax = rowOrder.length
  return (
    <Query
      query={fetchRestaurantPageScoreCardStats}
      variables={{
        stationId,
      }}
    >
      {({ loading, error, data }) => {
        if (loading || error) {
          return <CircularProgress />
        }

        const rows = Object.entries(removeTypename(data.scoreCardStats)).sort((a, b) => {
          const aValue = rowOrder.indexOf(a[0]) >= 0 ? rowOrder.indexOf(a[0]) : orderMax + 1
          const bValue = rowOrder.indexOf(b[0]) >= 0 ? rowOrder.indexOf(b[0]) : orderMax + 1
          if (aValue < bValue) return -1
          if (aValue > bValue) return 1
          return 0
        })
        return (
          <Paper className={css.scoreCard}>
            <Table className={css.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderName')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderRollingYear')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderLastMonth')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderThisMonth')}</TableCell>
                  <TableCell className={cx(css.tableHeading, css.actionCell)}>
                    {i18n.t('monthlyInfo.heading.isActionRequired')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: any, i: number) => {
                  const [category, values] = row
                  const { rollingYear, lastMonth, thisMonth } = values || {}

                  if (values) {
                    return (
                      <TableRow key={i}>
                        <TableCell className={css.tableCell}>{i18n.t(`scoreCard.${category}`)}</TableCell>
                        <TableCell className={css.tableCell}>
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle, getColorClassName(row, 'rollingYear'))} />
                            {rollingYear.actual}
                          </div>
                        </TableCell>
                        <TableCell className={css.tableCell}>
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle, getColorClassName(row, 'lastMonth'))} />
                            {lastMonth.actual}
                          </div>
                        </TableCell>
                        <TableCell className={css.tableCell}>
                          {values.thisMonth && (
                            <div className={css.cellValue}>
                              <span className={cx(css.colorCircle, getColorClassName(row, 'thisMonth'))} />
                              {thisMonth.actual}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className={css.actionCell}>
                          <Query
                            query={fetchMonthlyInfoScoreCard}
                            variables={{
                              stationId,
                              date,
                              type: 'ScoreCard',
                              row: category,
                            }}
                          >
                            {({ loading, error, data, refetch }) => {
                              if (loading || error) {
                                return <CircularProgress />
                              }

                              const info = get(data, 'monthlyInfo.0', {})
                              return (
                                <RequiresAction
                                  stationId={stationId}
                                  date={date}
                                  subject='Restaurant'
                                  isActionRequired={info.isActionRequired}
                                  monthlyInfoId={info.id}
                                  refetch={refetch}
                                />
                              )
                            }}
                          </Query>
                        </TableCell>
                      </TableRow>
                    )
                  } else {
                    return null
                  }
                })}
              </TableBody>
            </Table>
          </Paper>
        )
      }}
    </Query>
  )
}

@withUser
class RestaurantPage extends React.Component {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { user } = this.props
    const { imageOverlayIsOpen, selectedImage } = this.state
    return (
      <MonthlyInfoPage className={css.component} subject='Restaurant'>
        {({ stationId, salesIncentiveUnit, date, dateFormat, onDateChange, companyId, currentMonth }) => (
          <div className={css.content}>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />

            <SalesIncentive
              stationId={stationId}
              previousMonth={date}
              currentMonth={currentMonth}
              salesIncentiveUnit={salesIncentiveUnit}
            />

            <ScoreCardTable stationId={stationId} date={currentMonth} />

            <CustomerPath stationId={stationId} date={currentMonth} />

            <Images stationId={stationId} selectImage={this.selectImage} user={user} imageType={'monthlyinfo_hall'} />

            <Links stationId={stationId} linkType={'monthlyinfo_hall'} companyId={companyId} />
          </div>
        )}
      </MonthlyInfoPage>
    )
  }
}

export default RestaurantPage
